import { supabase } from '@/integrations/supabase/client';
import { VendorApplication, VendorApplicationRow } from '@/contracts/vendor-application';

// ─── Draft Management ───────────────────────────────────────────────────────

export interface DraftData {
  id: string;
  session_id: string;
  current_step: number;
  form_data: Partial<VendorApplication>;
  contact_email: string | null;
  company_name: string | null;
  last_activity_at: string;
  created_at: string;
}

export async function saveDraft(
  sessionId: string,
  step: number,
  formData: Partial<VendorApplication>
): Promise<void> {
  const { error } = await supabase
    .from('vendor_application_drafts')
    .upsert({
      session_id: sessionId,
      current_step: step,
      form_data: formData,
      contact_email: formData.contact_email || null,
      company_name: formData.company_name || null,
      last_activity_at: new Date().toISOString(),
    }, { onConflict: 'session_id' });

  if (error) {
    console.error('Failed to save draft:', error);
    // Don't throw - silent fail for auto-save
  }
}

export async function getDraft(sessionId: string): Promise<DraftData | null> {
  const { data, error } = await supabase
    .from('vendor_application_drafts')
    .select('*')
    .eq('session_id', sessionId)
    .single();

  if (error || !data) return null;
  return data as DraftData;
}

export async function deleteDraft(sessionId: string): Promise<void> {
  await supabase
    .from('vendor_application_drafts')
    .delete()
    .eq('session_id', sessionId);
}

export async function getIncompleteDrafts(): Promise<DraftData[]> {
  const { data, error } = await supabase
    .from('vendor_application_drafts')
    .select('*')
    .order('last_activity_at', { ascending: false });

  if (error) throw error;
  return (data || []) as DraftData[];
}

// ─── Applications ───────────────────────────────────────────────────────────

export async function submitVendorApplication(data: VendorApplication & { applicant_user_id?: string }): Promise<string> {
  // Get current user to attach to application
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  
  const { data: result, error } = await supabase
    .from('vendor_applications')
    .insert([{ ...data, applicant_user_id: userId } as any])
    .select('id')
    .single();
  
  if (error) throw error;
  
  // Notify admins via GHL
  await supabase.functions.invoke('notify-vendor-application-submitted', {
    body: { applicationId: result.id }
  }).catch(err => {
    console.error('Failed to send admin notification:', err);
    // Don't fail the application submission if notification fails
  });
  
  return result.id;
}

export async function getVendorApplications(
  status?: string
): Promise<VendorApplicationRow[]> {
  let query = supabase
    .from('vendor_applications')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (status && status !== 'all') {
    query = query.eq('status', status);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data as VendorApplicationRow[];
}

export async function updateApplicationStatus(
  id: string,
  status: string,
  reviewNotes?: string
): Promise<void> {
  const { data: user } = await supabase.auth.getUser();
  
  // Update application status
  const { data: application, error: appError } = await supabase
    .from('vendor_applications')
    .update({
      status,
      review_notes: reviewNotes,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.user?.id,
    })
    .eq('id', id)
    .select('contact_email')
    .single();
  
  if (appError) throw appError;
  
  // If approved, set is_vendor=true on the profile using user_id (reliable) or email (fallback)
  if (status === 'approved') {
    // First try to get applicant_user_id from the full application
    const { data: fullApp } = await supabase
      .from('vendor_applications')
      .select('applicant_user_id, contact_email')
      .eq('id', id)
      .single();
    
    if (fullApp?.applicant_user_id) {
      // Use reliable user_id match
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_vendor: true })
        .eq('user_id', fullApp.applicant_user_id);
      
      if (profileError) {
        console.error('Failed to update vendor status by user_id:', profileError);
      }
    } else if (fullApp?.contact_email) {
      // Fallback to email match for legacy applications
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_vendor: true })
        .eq('email', fullApp.contact_email);
      
      if (profileError) {
        console.error('Failed to update vendor status by email:', profileError);
      }
    }
  }
  
  // Notify vendor of decision via GHL
  if (status === 'approved' || status === 'rejected') {
    await supabase.functions.invoke('notify-vendor-application-decision', {
      body: { applicationId: id, decision: status, reviewNotes }
    }).catch(err => {
      console.error('Failed to send vendor decision notification:', err);
      // Don't fail the status update if notification fails
    });
  }
}

export async function uploadVendorAsset(
  file: File,
  applicationId: string,
  type: 'logo' | 'screenshot'
): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${applicationId}/${type}-${Date.now()}.${fileExt}`;
  
  const { error: uploadError } = await supabase.storage
    .from('vendor-applications')
    .upload(fileName, file);
  
  if (uploadError) throw uploadError;
  
  const { data } = supabase.storage
    .from('vendor-applications')
    .getPublicUrl(fileName);
  
  return data.publicUrl;
}
